import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { ChefHat, Clock, CheckCircle, Printer, Volume2, VolumeX, Wine, UtensilsCrossed, MessageSquarePlus, X, AlertTriangle, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { printKitchenTicket } from '../components/PrintTickets';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_NAMES = {
  grab: 'GRAB', foodpanda: 'FOODPANDA', line_man: 'LINE MAN',
  uber_eats: 'UBER EATS', deliveroo: 'DELIVEROO', just_eat: 'JUST EAT',
  glovo: 'GLOVO', wolt: 'WOLT', bolt_food: 'BOLT FOOD',
  doordash: 'DOORDASH', grubhub: 'GRUBHUB',
  talabat: 'TALABAT', careem: 'CAREEM', noon_food: 'NOON',
  rappi: 'RAPPI', ifood: 'IFOOD', custom_delivery: 'LIVRAISON',
};

const PLATFORM_COLORS = {
  grab: '#00B14F', foodpanda: '#D70F64', line_man: '#00C300',
  uber_eats: '#222', deliveroo: '#00CCBC', just_eat: '#FF8000',
  glovo: '#FFC244', wolt: '#009DE0', bolt_food: '#34D186',
  doordash: '#FF3008', grubhub: '#F63440',
  talabat: '#FF5800', careem: '#49B44D', noon_food: '#C4A000',
  rappi: '#FF441F', ifood: '#EA1D2C', custom_delivery: '#8B5CF6',
};

function getPlatformLabel(order) {
  const src = order.order_source;
  if (src && src !== 'dine_in' && src !== 'takeaway') return PLATFORM_NAMES[src] || src.toUpperCase();
  if (order.is_takeaway) return 'EMPORTER';
  return `T${order.table_id}`;
}

function getPlatformColor(src) {
  return PLATFORM_COLORS[src] || null;
}

function getElapsedSeconds(dateStr) {
  if (!dateStr) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000));
}

function formatTimer(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m >= 60) return `${Math.floor(m / 60)}h${(m % 60).toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function urgencyLevel(seconds) {
  const mins = seconds / 60;
  if (mins > 20) return 2;
  if (mins > 10) return 1;
  return 0;
}

const urgencyBorder = ['border-[#E5DCD3]', 'border-[#F59E0B]', 'border-[#EF4444]'];
const urgencyBg = ['bg-white', 'bg-[#FFFBEB]', 'bg-[#FEF2F2]'];

export default function KitchenPage() {
  const [orders, setOrders] = useState([]);
  const [itemCatMap, setItemCatMap] = useState({});
  const [catSections, setCatSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [soundOn, setSoundOn] = useState(true);
  const [tick, setTick] = useState(0);
  const audioRef = useRef(null);
  const sseRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, catRes] = await Promise.all([
        axios.get(`${API}/kitchen/orders`),
        axios.get(`${API}/kitchen/menu-categories`),
      ]);
      setOrders(ordersRes.data);
      if (catRes.data.items) {
        setItemCatMap(catRes.data.items);
        setCatSections(catRes.data.sections || {});
      }
    } catch (e) {
      console.error('Fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Tick every second for live timers
  useEffect(() => {
    const t = setInterval(() => setTick(k => k + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    fetchData();
    const connectSSE = () => {
      const es = new EventSource(`${API}/events/kitchen`);
      sseRef.current = es;
      es.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          if (event.type === 'heartbeat' || event.type === 'connected') return;
          if (['new_order', 'order_updated', 'status_update', 'section_ready'].includes(event.type)) {
            fetchData();
            if ((event.type === 'new_order' || event.type === 'order_updated') && soundOn && audioRef.current) {
              audioRef.current.play().catch(() => {});
            }
            if (event.type === 'new_order') {
              toast.info(`Nouvelle commande - Table ${event.table_id}`);
            }
            if (event.type === 'order_updated') {
              toast.warning(`Commande modifiee - Table ${event.table_id}`);
            }
          }
        } catch (err) {
          console.error('SSE parse error:', err);
        }
      };
      es.onerror = () => { es.close(); setTimeout(connectSSE, 3000); };
    };
    connectSSE();
    return () => { if (sseRef.current) sseRef.current.close(); };
  }, [fetchData, soundOn]);

  const markSectionReady = async (orderId, section) => {
    try {
      const { data } = await axios.patch(`${API}/orders/${orderId}/section-ready?section=${section}`);
      if (data.all_ready) {
        toast.success('Commande prete ! (envoyee au serveur)');
      } else {
        toast.success(section === 'bar' ? 'Bar OK !' : 'Cuisine OK !');
      }
      fetchData();
    } catch (e) {
      toast.error('Erreur lors de la mise a jour');
    }
  };

  const markItemReady = async (orderId, itemIndex) => {
    try {
      const { data } = await axios.patch(`${API}/orders/${orderId}/item-ready?item_index=${itemIndex}`);
      if (data.all_ready) {
        toast.success('Tous les articles sont prets !');
      }
      fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const printTicket = (order, items) => {
    printKitchenTicket({
      tableId: order.table_id, items, notes: order.notes,
      orderId: order.id, createdAt: order.created_at,
      serverName: order.server_name || '',
    });
  };

  const sendComment = async (orderId, comment, source) => {
    try {
      await axios.post(`${API}/orders/${orderId}/kitchen-comment`, { comment, source });
      toast.success('Commentaire envoye !');
      fetchData();
    } catch (e) {
      toast.error('Erreur envoi commentaire');
    }
  };

  const splitOrder = useCallback((order) => {
    const kitchenItems = [];
    const barItems = [];
    for (const item of order.items || []) {
      const catId = itemCatMap[item.item_id] || 'mains';
      const section = catSections[catId] || 'cuisine';
      if (section === 'bar') barItems.push(item);
      else kitchenItems.push(item);
    }
    return { kitchenItems, barItems };
  }, [itemCatMap, catSections]);

  const kitchenOrders = orders.filter(o => splitOrder(o).kitchenItems.length > 0)
    .map(o => ({ ...o, ...splitOrder(o), _hasKitchen: true, _hasBar: splitOrder(o).barItems.length > 0 }));

  const barOrders = orders.filter(o => splitOrder(o).barItems.length > 0)
    .map(o => ({ ...o, ...splitOrder(o), _hasKitchen: splitOrder(o).kitchenItems.length > 0, _hasBar: true }));

  return (
    <div className="min-h-screen bg-[#F5F0EB]" data-testid="kitchen-page">
      <audio ref={audioRef} preload="auto"
        src="data:audio/wav;base64,UklGRiQDAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQADAABkAKgA3AAQASgBQAFIAUABKAEIAdgAqABkACAA3ACYAFQAEADc/6j/eP9I/yD/AP/o/tD+wP64/rj+wP7Q/uj+AP8g/0j/eP+o/9z/EABIAHgAoADIAOAAIAE=" />

      <header className="bg-[#1A1A1A] px-4 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <ChefHat className="w-7 h-7 text-[#D4A853]" />
          <h1 className="text-xl font-bold text-white tracking-wide" data-testid="kitchen-title">CUISINE & BAR</h1>
          <span className="bg-[#E16833] text-white text-xs font-bold px-2.5 py-1 rounded-full" data-testid="kitchen-order-count">
            {orders.length} en cours
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setSoundOn(!soundOn)}
            className="text-[#A0A0A0] hover:text-white transition-colors p-2"
            data-testid="kitchen-sound-toggle">
            {soundOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          <button onClick={fetchData}
            className="text-sm text-[#A0A0A0] hover:text-white transition-colors px-3 py-1.5 rounded-lg border border-[#333]"
            data-testid="kitchen-refresh-btn">
            Rafraichir
          </button>
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center h-[70vh]">
          <div className="animate-spin w-10 h-10 border-4 border-[#D4A853] border-t-transparent rounded-full" />
        </div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[70vh] text-[#6E5E52]" data-testid="kitchen-empty">
          <ChefHat className="w-20 h-20 mb-4 opacity-20" />
          <p className="text-xl font-medium">Aucune commande en attente</p>
          <p className="text-sm mt-1 opacity-60">Les nouvelles commandes apparaitront ici automatiquement</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 h-[calc(100vh-56px)]">
          {/* CUISINE Column */}
          <div className="border-r border-[#D4C9BC] overflow-y-auto" data-testid="kitchen-section-cuisine">
            <div className="sticky top-0 z-10 bg-[#2D241D] px-4 py-2.5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UtensilsCrossed className="w-5 h-5 text-[#E16833]" />
                <h2 className="text-base font-bold text-white">CUISINE</h2>
              </div>
              <span className="bg-[#E16833]/20 text-[#E16833] text-xs font-bold px-2 py-0.5 rounded-full">
                {kitchenOrders.length}
              </span>
            </div>
            <div className="p-3 space-y-3">
              {kitchenOrders.length === 0 ? (
                <div className="text-center py-12 text-[#6E5E52] opacity-50">
                  <UtensilsCrossed className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Aucun plat en attente</p>
                </div>
              ) : kitchenOrders.map(order => (
                <SectionOrderCard key={order.id} order={order} items={order.kitchenItems}
                  section="kitchen" otherSectionReady={order.bar_ready}
                  thisSectionReady={order.kitchen_ready}
                  onReady={markSectionReady} onItemReady={markItemReady}
                  onPrint={printTicket} onComment={sendComment} tick={tick} />
              ))}
            </div>
          </div>

          {/* BAR Column */}
          <div className="overflow-y-auto" data-testid="kitchen-section-bar">
            <div className="sticky top-0 z-10 bg-[#1B3A4B] px-4 py-2.5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wine className="w-5 h-5 text-[#60A5FA]" />
                <h2 className="text-base font-bold text-white">BAR</h2>
              </div>
              <span className="bg-[#60A5FA]/20 text-[#60A5FA] text-xs font-bold px-2 py-0.5 rounded-full">
                {barOrders.length}
              </span>
            </div>
            <div className="p-3 space-y-3">
              {barOrders.length === 0 ? (
                <div className="text-center py-12 text-[#6E5E52] opacity-50">
                  <Wine className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Aucune boisson en attente</p>
                </div>
              ) : barOrders.map(order => (
                <SectionOrderCard key={order.id} order={order} items={order.barItems}
                  section="bar" otherSectionReady={order.kitchen_ready}
                  thisSectionReady={order.bar_ready}
                  onReady={markSectionReady} onItemReady={markItemReady}
                  onPrint={printTicket} onComment={sendComment} tick={tick} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionOrderCard({ order, items, section, thisSectionReady, otherSectionReady, onReady, onItemReady, onPrint, onComment, tick }) {
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentText, setCommentText] = useState('');
  const elapsed = getElapsedSeconds(order.created_at);
  const urg = urgencyLevel(elapsed);
  const isBar = section === 'bar';
  const accentColor = isBar ? '#60A5FA' : '#E16833';
  const hasOtherSection = isBar ? order._hasKitchen : order._hasBar;
  const comments = order.kitchen_comments || [];
  const hasModifications = (order.modifications || []).length > 0;

  // Map items to their original indices in the full order items array
  const itemIndices = [];
  const orderItems = order.items || [];
  for (let i = 0; i < items.length; i++) {
    const idx = orderItems.findIndex((oi, j) =>
      oi.item_id === items[i].item_id && oi.name === items[i].name && !itemIndices.includes(j)
    );
    itemIndices.push(idx >= 0 ? idx : i);
  }

  const handleSend = () => {
    if (!commentText.trim()) return;
    onComment(order.id, commentText.trim(), isBar ? 'bar' : 'cuisine');
    setCommentText('');
    setShowCommentInput(false);
  };

  if (thisSectionReady) {
    return (
      <div className="rounded-2xl border-2 border-[#2D6A4F]/30 bg-[#E8F5E9] p-3 flex items-center justify-between opacity-60"
        data-testid={`kitchen-order-done-${section}-${order.id}`}>
        <div className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-[#2D6A4F]" />
          <span className="font-bold text-[#2D241D]">{getPlatformLabel(order)}</span>
          <span className="text-xs text-[#2D6A4F] font-medium">{isBar ? 'Bar OK' : 'Cuisine OK'}</span>
        </div>
        {hasOtherSection && !otherSectionReady && (
          <span className="text-[10px] text-[#F59E0B] font-medium">En attente {isBar ? 'cuisine' : 'bar'}...</span>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border-2 ${urgencyBorder[urg]} ${urgencyBg[urg]} overflow-hidden shadow-sm ${urg === 2 ? 'animate-pulse-subtle' : ''}`}
      data-testid={`kitchen-order-${section}-${order.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#F3EDE5]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-lg font-black text-[#2D241D]">{getPlatformLabel(order)}</span>
          {order.order_number && (
            <span className="px-2 py-0.5 rounded-md bg-[#1B3A4B] text-white text-[10px] font-bold tracking-wider">
              {order.order_number}
            </span>
          )}
          {order.order_source && order.order_source !== 'dine_in' && order.order_source !== 'takeaway' && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold"
              style={{ backgroundColor: `${getPlatformColor(order.order_source) || '#888'}15`, color: getPlatformColor(order.order_source) || '#888' }}>
              {order.grab_order_id || 'Livraison'}
            </span>
          )}
          {order.status === 'received' && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#E16833]/15 text-[#E16833]">
              nouveau
            </span>
          )}
          {hasModifications && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#F59E0B]/15 text-[#F59E0B] flex items-center gap-0.5"
              data-testid={`kitchen-modified-badge-${order.id}`}>
              <AlertTriangle className="w-3 h-3" /> MODIFIE
            </span>
          )}
          {otherSectionReady && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#2D6A4F]/15 text-[#2D6A4F] flex items-center gap-0.5">
              <CheckCircle className="w-3 h-3" /> {isBar ? 'Cuisine OK' : 'Bar OK'}
            </span>
          )}
        </div>
        {/* Live timer */}
        <div className={`flex items-center gap-1.5 font-mono text-sm font-bold rounded-lg px-2 py-1 ${
          urg === 2 ? 'bg-[#EF4444] text-white' : urg === 1 ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'text-[#6E5E52]'
        }`} data-testid={`kitchen-timer-${section}-${order.id}`}>
          <Clock className="w-4 h-4" />
          {formatTimer(elapsed)}
        </div>
      </div>

      {/* Notes */}
      {order.notes && (
        <div className="px-4 py-2 bg-[#FFF3E0] border-b border-[#FFCC80]">
          <p className="text-sm font-bold text-[#E65100]">!! {order.notes}</p>
        </div>
      )}

      {/* Items with per-item ready buttons */}
      <div className="px-4 py-3 space-y-1">
        {items.map((item, idx) => {
          const isReady = item.is_ready;
          const originalIdx = itemIndices[idx];
          return (
            <div key={idx} className={`flex items-center justify-between py-1.5 ${isReady ? 'opacity-40' : ''}`}
              data-testid={`kitchen-item-${section}-${order.id}-${idx}`}>
              <span className={`text-base font-bold text-[#2D241D] ${isReady ? 'line-through' : ''}`}>
                <span style={{ color: accentColor }} className="mr-1.5">{item.quantity}x</span>
                {item.name}
              </span>
              <button
                onClick={() => !isReady && onItemReady(order.id, originalIdx)}
                disabled={isReady}
                className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                  isReady
                    ? 'bg-[#2D6A4F] text-white cursor-default'
                    : 'bg-[#E5DCD3] text-[#6E5E52] hover:bg-[#2D6A4F] hover:text-white active:scale-90'
                }`}
                data-testid={`kitchen-item-ready-btn-${section}-${order.id}-${idx}`}>
                <Check className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Server info */}
      {order.server_name && (
        <div className="px-4 pb-2">
          <span className="text-xs text-[#6E5E52]">Serveur: {order.server_name}</span>
        </div>
      )}

      {/* Comments */}
      {comments.length > 0 && (
        <div className="px-4 pb-2 space-y-1">
          {comments.map((c, i) => (
            <div key={i} className={`text-xs rounded-lg px-2.5 py-1.5 ${
              c.source === 'bar' ? 'bg-[#1B3A4B]/10 text-[#1B3A4B]' : 'bg-[#E16833]/10 text-[#E16833]'
            }`}>
              <span className="font-bold">{c.source === 'bar' ? 'Bar' : 'Cuisine'}:</span> {c.text}
            </div>
          ))}
        </div>
      )}

      {/* Comment input */}
      {showCommentInput && (
        <div className="px-3 pb-2">
          <div className="flex gap-1.5">
            <input type="text" value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ex: Plus de Mojito..."
              className="flex-1 text-sm rounded-xl border border-[#E5DCD3] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#D4A853]/50"
              autoFocus data-testid={`kitchen-comment-input-${section}-${order.id}`} />
            <Button onClick={handleSend} size="sm"
              className="bg-[#D4A853] hover:bg-[#C49A48] text-white rounded-xl px-3 h-[38px]"
              data-testid={`kitchen-comment-send-${section}-${order.id}`}>
              Envoyer
            </Button>
            <button onClick={() => { setShowCommentInput(false); setCommentText(''); }}
              className="text-[#6E5E52] hover:text-[#2D241D] px-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-3 py-3 bg-[#F3EDE5] flex gap-2">
        <Button onClick={() => onReady(order.id, section)}
          className={`flex-1 text-white rounded-xl h-12 text-sm font-bold ${
            isBar ? 'bg-[#1B3A4B] hover:bg-[#0F2A38]' : 'bg-[#2D6A4F] hover:bg-[#1B4332]'
          }`}
          data-testid={`kitchen-ready-${section}-${order.id}`}>
          <CheckCircle className="w-4 h-4 mr-1.5" />
          {isBar ? 'TOUT PRET - BAR' : 'TOUT PRET - CUISINE'}
        </Button>
        <button onClick={() => setShowCommentInput(!showCommentInput)}
          className={`h-12 w-12 rounded-xl flex items-center justify-center transition-colors flex-shrink-0 ${
            showCommentInput ? 'bg-[#D4A853] text-white' : 'bg-[#D4A853]/10 text-[#D4A853] hover:bg-[#D4A853] hover:text-white'
          }`}
          data-testid={`kitchen-comment-btn-${section}-${order.id}`}>
          <MessageSquarePlus className="w-5 h-5" />
        </button>
        <button onClick={() => onPrint(order, items)}
          className="h-12 w-12 rounded-xl bg-[#D4A853]/10 text-[#D4A853] flex items-center justify-center hover:bg-[#D4A853] hover:text-white transition-colors flex-shrink-0"
          data-testid={`kitchen-print-${section}-${order.id}`}>
          <Printer className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
